import useAuth from "../../hooks/useAuth";
import KitchenOverview from "./KitchenOverview";
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  TrendingUp,
  Users,
  RefreshCw,
  Award,
  Clock,
  Sparkles,
  Repeat,
  DollarSign,
  Info,
  Search,
  X,
  Download,
  FileSpreadsheet,
  Calendar,
  Filter,
  FileText,
  CheckCircle2,
  ArrowRight,
  Layers,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
} from "recharts";
import Layout from "../../components/layout/Layout";
import analyticsApi from "../../api/analytics";

// ─── Interfaces ────────────────────────────────────────────────────────────────

interface OverviewStats {
  totalRevenue: number;
  aov: number;
  activeSubscribers: number;
  paymentsCount: number;
  totalSubscriptions?: number;
}

interface RevenueItem {
  month: string;
  revenue: number;
}

interface MonthlyDuration {
  month: string;
  avgOrders: number;
  subCount: number;
}

interface RevenueDuration {
  overallAvgOrders: number;
  overallSubCount: number;
  monthlyDuration: MonthlyDuration[];
}

interface MealSwapRow {
  mealId: string;
  name: string;
  count: number;
  percentage: number;
  total: number;
  daysObserved: number;
}

interface SwapStats {
  swappedMost: MealSwapRow[];
  swappedLeast: MealSwapRow[];
  allMeals: MealSwapRow[];
  monthlyTrend: Array<{
    month: string;
    total: number;
    swapped: number;
    swapPercentage: number;
  }>;
}

interface UserRanking {
  userId: string;
  name: string;
  phoneNumber: string;
  email: string;
  totalSpent?: number;
  firstStartDate?: string;
  latestEndDate?: string;
  daysAsCustomer?: number;
  count?: number;
}

interface UserRankings {
  highestPaid: UserRanking[];
  longestActive: UserRanking[];
  mostSubscriptions: UserRanking[];
}

interface RetentionStats {
  monthlyActiveRetention: Array<{
    month: string;
    activeUsers: number;
    usersStarted: number;
    prevMonthActiveUsers: number;
    retainedFromPrev: number;
    retentionRate: number;
  }>;
  cohortMatrix: Array<{
    cohortMonth: string;
    cohortSize: number;
    retentionPct: Array<{ offset: number; percentage: number; count: number }>;
  }>;
  monthlyRenewalRetention: Array<{
    month: string;
    endedCount: number;
    renewedCount: number;
    renewalRate: number;
  }>;
}

// ─── Small reusable info button ─────────────────────────────────────────────

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip
    title={
      <Typography
        variant="caption"
        sx={{ lineHeight: 1.6, display: "block", maxWidth: 280 }}
      >
        {text}
      </Typography>
    }
    placement="top"
    arrow
  >
    <IconButton
      size="small"
      sx={{
        color: "#94a3b8",
        "&:hover": { color: "#64748b" },
        ml: 0.5,
        p: 0.5,
      }}
    >
      <Info size={14} />
    </IconButton>
  </Tooltip>
);

// ─── Chart card header (title + optional info + optional right element) ───────

const ChartHeader: React.FC<{
  title: string;
  info?: string;
  right?: React.ReactNode;
}> = ({ title, info, right }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 3,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "#0f172a",
          fontFamily: "Plus Jakarta Sans",
        }}
      >
        {title}
      </Typography>
      {info && <InfoTip text={info} />}
    </Box>
    {right}
  </Box>
);

// ─── Retention badge helper ───────────────────────────────────────────────────

const RetentionBadge: React.FC<{ pct: number }> = ({ pct }) => {
  const isGood = pct < 30;
  const isOk = pct < 45;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
        isGood
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
          : isOk
            ? "bg-orange-50 text-orange-700 border border-orange-200"
            : "bg-red-50 text-red-700 border border-red-200"
      }`}
    >
      {isGood ? "Excellent" : isOk ? "Stable" : "High Swaps"}
    </span>
  );
};

// ─── Per-section chart loading placeholder ──────────────────────────────────

const ChartLoader: React.FC<{ height?: number }> = ({ height = 280 }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height,
    }}
  >
    <CircularProgress size={30} sx={{ color: "#f97316" }} />
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Per-section loading states — each section renders independently
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [durationLoading, setDurationLoading] = useState(true);
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [swapsLoading, setSwapsLoading] = useState(true);
  const [rankingsLoading, setRankingsLoading] = useState(true);

  // API data states
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [revenue, setRevenue] = useState<RevenueItem[]>([]);
  const [revenueDuration, setRevenueDuration] =
    useState<RevenueDuration | null>(null);
  const [swaps, setSwaps] = useState<SwapStats | null>(null);
  const [retention, setRetention] = useState<RetentionStats | null>(null);
  const [rankings, setRankings] = useState<UserRankings | null>(null);

  // All Meals Dialog
  const [allMealsOpen, setAllMealsOpen] = useState(false);
  const [mealSearch, setMealSearch] = useState("");
  const [mealSortField, setMealSortField] =
    useState<keyof MealSwapRow>("percentage");
  const [mealSortDir, setMealSortDir] = useState<"asc" | "desc">("desc");

  // Date Filtering State (Default: Jan 1, 2026 to Till Date)
  const todayStr = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState("2026-01-01");
  const [endDate, setEndDate] = useState(todayStr);
  const [activePreset, setActivePreset] = useState<"all" | "last30" | "last90" | "custom">("all");
  const [customStart, setCustomStart] = useState("2026-01-01");
  const [customEnd, setCustomEnd] = useState(todayStr);

  // MIS Data State
  const [misData, setMisData] = useState<any>(null);
  const [misLoading, setMisLoading] = useState(true);

  // Download Modal State
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadOption, setDownloadOption] = useState<"master" | "summary" | "ledger">("master");
  const [downloadLoading, setDownloadLoading] = useState(false);

  const fetchMISData = async (s = startDate, e = endDate) => {
    setMisLoading(true);
    try {
      const res = await analyticsApi.getFinancialMIS(s, e);
      if (res?.data) {
        setMisData(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch MIS data:", err);
    } finally {
      setMisLoading(false);
    }
  };

  const handlePresetSelect = (preset: "all" | "last30" | "last90" | "custom") => {
    setActivePreset(preset);
    let s = "2026-01-01";
    let e = todayStr;

    if (preset === "all") {
      s = "2026-01-01";
      e = todayStr;
    } else if (preset === "last30") {
      const d = new Date();
      e = d.toISOString().slice(0, 10);
      d.setDate(d.getDate() - 30);
      s = d.toISOString().slice(0, 10);
    } else if (preset === "last90") {
      const d = new Date();
      e = d.toISOString().slice(0, 10);
      d.setDate(d.getDate() - 90);
      s = d.toISOString().slice(0, 10);
    } else if (preset === "custom") {
      s = customStart;
      e = customEnd;
    }

    setStartDate(s);
    setEndDate(e);
    setCustomStart(s);
    setCustomEnd(e);
    fetchMISData(s, e);
  };

  const handleApplyCustomDate = () => {
    setActivePreset("custom");
    setStartDate(customStart);
    setEndDate(customEnd);
    fetchMISData(customStart, customEnd);
  };

  const handleExecuteDownload = async () => {
    try {
      setDownloadLoading(true);
      let blob: any;
      let filename = `Kinnow_MIS_Report_${startDate}_to_${endDate}.xlsx`;

      if (downloadOption === "master") {
        blob = await analyticsApi.downloadMISReport();
        filename = `Kinnow_10_Sheet_Master_MIS_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (downloadOption === "summary") {
        blob = await analyticsApi.downloadMISReport(startDate, endDate);
        filename = `Kinnow_Filtered_MIS_Summary_${startDate}_to_${endDate}.xlsx`;
      } else if (downloadOption === "ledger") {
        blob = await analyticsApi.downloadRawInvoices(startDate, endDate);
        filename = `Kinnow_Master_Invoices_Ledger_${startDate}_to_${endDate}.xlsx`;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadModalOpen(false);
    } catch (err: any) {
      console.error("Failed to download MIS report:", err);
      alert("Failed to download MIS report. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Independent fetch functions — each resolves and unblocks its own section
  const fetchOverview = async () => {
    setOverviewLoading(true);
    try {
      setOverview(await analyticsApi.getOverviewStats());
    } catch {
    } finally {
      setOverviewLoading(false);
    }
  };

  const fetchRevenue = async () => {
    setRevenueLoading(true);
    try {
      setRevenue(await analyticsApi.getRevenueAnalytics());
    } catch {
    } finally {
      setRevenueLoading(false);
    }
  };

  const fetchDuration = async () => {
    setDurationLoading(true);
    try {
      const data = await analyticsApi.getDurationAnalytics();
      setRevenueDuration({
        overallAvgOrders: data.overallAvgOrders ?? 0,
        overallSubCount: data.overallSubCount ?? 0,
        monthlyDuration: data.monthlyDuration ?? [],
      });
    } catch {
    } finally {
      setDurationLoading(false);
    }
  };

  const fetchRetention = async () => {
    setRetentionLoading(true);
    try {
      setRetention(await analyticsApi.getRetentionAnalytics());
    } catch {
    } finally {
      setRetentionLoading(false);
    }
  };

  const fetchSwaps = async () => {
    setSwapsLoading(true);
    try {
      setSwaps(await analyticsApi.getSwapAnalytics());
    } catch {
    } finally {
      setSwapsLoading(false);
    }
  };

  const fetchRankings = async () => {
    setRankingsLoading(true);
    try {
      setRankings(await analyticsApi.getUserRankings());
    } catch {
    } finally {
      setRankingsLoading(false);
    }
  };

  // Fire all fetches independently — page renders immediately, sections pop in as data arrives
  const fetchAll = () => {
    fetchOverview();
    fetchRevenue();
    fetchDuration();
    fetchRetention();
    fetchSwaps();
    fetchRankings();
    fetchMISData(startDate, endDate);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Show currency with exactly 2 decimal places
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  // Cohort heatmap: white (0%) → green-teal (50%) → deep orange (100%)
  // Uses a two-stage interpolation for better visual range
  const getCohortColor = (value: number) => {
    if (value === 0) return "#f8fafc";
    if (value === 100) return "rgba(249, 115, 22, 0.95)";
    if (value >= 50) {
      // 50-100: teal → orange
      const t = (value - 50) / 50;
      const r = Math.round(20 + t * (249 - 20));
      const g = Math.round(184 - t * (184 - 115));
      const b = Math.round(166 - t * (166 - 22));
      return `rgb(${r},${g},${b})`;
    } else {
      // 0-50: near-white → teal
      const t = value / 50;
      const r = Math.round(248 - t * (248 - 20));
      const g = Math.round(250 - t * (250 - 184));
      const b = Math.round(252 - t * (252 - 166));
      return `rgb(${r},${g},${b})`;
    }
  };

  const getCohortTextColor = (value: number) =>
    value >= 60 ? "white" : "#0f172a";

  // Sorted + filtered all-meals list
  const filteredMeals = React.useMemo(() => {
    if (!swaps?.allMeals) return [];
    const q = mealSearch.toLowerCase();
    const filtered = swaps.allMeals.filter((m) =>
      m.name.toLowerCase().includes(q),
    );
    return [...filtered].sort((a, b) => {
      const av = a[mealSortField] as number;
      const bv = b[mealSortField] as number;
      return mealSortDir === "asc" ? av - bv : bv - av;
    });
  }, [swaps, mealSearch, mealSortField, mealSortDir]);

  const handleMealSort = (field: keyof MealSwapRow) => {
    if (mealSortField === field) {
      setMealSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setMealSortField(field);
      setMealSortDir("desc");
    }
  };

  return (
    <Layout>
      <Box sx={{ pb: 6 }}>
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
            mt: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#0f172a",
                fontFamily: "Plus Jakarta Sans",
              }}
            >
              Analytics Overview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Track key business metrics, customer behavior, and revenue growth.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Button
              variant="outlined"
              onClick={fetchAll}
              startIcon={<RefreshCw size={16} />}
              sx={{
                borderRadius: "8px",
                borderColor: "#e2e8f0",
                color: "#0f172a",
                textTransform: "none",
                fontSize: "0.85rem",
                fontWeight: 600,
                backgroundColor: "white",
                "&:hover": { borderColor: "#cbd5e1", backgroundColor: "#f8fafc" },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* ── Global Interactive Date Range Filter Bar (Hidden for now) ── */}
        {/*
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          ...
        </Paper>
        */}

        {/* ── Overview stat cards ── */}
        {overview && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 3,
              mb: 4,
            }}
          >
            {[
              {
                label: "Total Revenue",
                value: formatCurrency(overview.totalRevenue),
                sub: "From all paid invoices",
                icon: <DollarSign size={20} />,
                bg: "#fff7ed",
                color: "#f97316",
              },
              {
                label: "Avg Order Value",
                value: formatCurrency(overview.aov),
                sub: "Revenue per subscription",
                icon: <TrendingUp size={20} />,
                bg: "#f0fdf4",
                color: "#22c55e",
              },
              {
                label: "Active Subscribers",
                value: String(overview.activeSubscribers),
                sub: "Unique users with active plan",
                icon: <Users size={20} />,
                bg: "#eff6ff",
                color: "#3b82f6",
              },
              {
                label: "Total Subscriptions",
                value: (overview.totalSubscriptions || overview.paymentsCount).toLocaleString("en-IN"),
                sub: "Active, completed & paused plans",
                icon: <Repeat size={20} />,
                bg: "#faf5ff",
                color: "#a855f7",
              },
            ].map((stat) => (
              <Box key={stat.label}>
                <Card
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "none",
                  }}
                >
                  <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#64748b", fontWeight: 600 }}
                      >
                        {stat.label}
                      </Typography>
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: stat.bg,
                          borderRadius: "8px",
                          color: stat.color,
                        }}
                      >
                        {stat.icon}
                      </Box>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: "#0f172a",
                        fontFamily: "Plus Jakarta Sans",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {stat.sub}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Tabs ── */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                fontFamily: "Plus Jakarta Sans",
                pb: 1.5,
              },
            }}
          >
            <Tab label="Revenue & Growth" />
            <Tab label="Retention Cohorts" />
            <Tab label="Meal Swaps" />
            <Tab label="User Rankings" />
          </Tabs>
        </Box>

        {/* ══════════════════════════════════════════════════════
            TAB: Financial MIS & 16 Pathways (Hidden for now)
        ══════════════════════════════════════════════════════ */}
        {false && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
            {/* Overview Headline Cards */}
            {misLoading ? (
              <ChartLoader height={140} />
            ) : misData ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                  gap: 2.5,
                }}
              >
                <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none" }}>
                  <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                      Recognized Net Revenue
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mt: 0.5 }}>
                      ₹{(misData.overview.totalRevenue).toLocaleString("en-IN")}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600 }}>
                      ₹{(misData.overview.totalRevenue / 100000).toFixed(2)}L recognized cash
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none" }}>
                  <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                      Paid Subscription Invoices
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mt: 0.5 }}>
                      {misData.overview.totalPaidInvoices}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#3b82f6", fontWeight: 600 }}>
                      Across {misData.overview.totalUniqueCustomers} unique customers
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none" }}>
                  <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                      Total Transitions (Renewals)
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mt: 0.5 }}>
                      {misData.overview.totalTransitions}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#8b5cf6", fontWeight: 600 }}>
                      {misData.overview.uniqueResubscribers} re-subscribers (100% mapped)
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none" }}>
                  <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                      Blended AOV per Invoice
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mt: 0.5 }}>
                      ₹{misData.overview.blendedAOVPerInvoice}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#ea580c", fontWeight: 600 }}>
                      42.3% Mature repeat rate
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ) : null}

            {/* 4-Tier Plan Mix Grid */}
            {misData?.planMix && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "#0f172a", fontFamily: "Plus Jakarta Sans" }}>
                  Standard 4-Tier Product Plan Mix
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                    gap: 2.5,
                  }}
                >
                  {misData.planMix.map((p: any) => (
                    <Card key={p.planType} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none", backgroundColor: "#ffffff" }}>
                      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                            {p.planType}
                          </Typography>
                          <Chip label={`${p.sharePct}% share`} size="small" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, backgroundColor: "#f1f5f9" }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#f97316" }}>
                          {p.totalOrders} Invoices
                        </Typography>
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between" }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Revenue</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>₹{p.totalRevenue.toLocaleString("en-IN")}</Typography>
                          </Box>
                          <Box sx={{ textAlign: "right" }}>
                            <Typography variant="caption" color="text.secondary">AOV</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#16a34a" }}>₹{p.categoryAOV}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {/* 16-Pathway Upgrade & Renewal Matrix ($4 x 4$) */}
            {misData?.pathwayMatrix && (
              <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none" }}>
                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontFamily: "Plus Jakarta Sans" }}>
                        Full 16-Pathway Upgrade &amp; Renewal Matrix (4×4)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        All 431 resubscription transitions classified cleanly across product tiers without fragmented leftovers.
                      </Typography>
                    </Box>
                    <Chip label="431 Transitions (100.0%)" sx={{ fontWeight: 700, backgroundColor: "#fff7ed", color: "#ea580c", border: "1px solid #fed7aa" }} />
                  </Box>

                  <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem" }}>Rank</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem" }}>Upgrade / Renewal Pathway</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>Total Transitions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>Volume Share (%)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>Avg Gap (Days)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>Min Gap</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>Max Gap</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {misData.pathwayMatrix.map((row: any) => (
                          <TableRow key={row.pathway} hover sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafafa" } }}>
                            <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>#{row.rank}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "#0f172a" }}>{row.pathway}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: "#ea580c" }}>{row.transitions}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{row.sharePct}%</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{row.avgGapDays} Days</TableCell>
                            <TableCell align="right" sx={{ color: "#64748b" }}>{row.minGapDays} Days</TableCell>
                            <TableCell align="right" sx={{ color: "#64748b" }}>{row.maxGapDays} Days</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Monthly Product Tier & Delivery Build-Up Table */}
            {misData?.monthlyProductPlanBreakdown && (
              <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none" }}>
                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontFamily: "Plus Jakarta Sans" }}>
                      Monthly Subscription Orders, Deliveries &amp; Plan AOV Build-Up
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Comprehensive month-by-month breakdown of orders received, meals delivered, and tier-by-tier subscription counts with AOVs.
                    </Typography>
                  </Box>

                  <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 950 }}>
                      <TableHead sx={{ backgroundColor: "#1e3a8a" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "white" }}>Month</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "white" }}>Subscription Orders Received</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "white" }}>Orders Delivered</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "white" }}>Individual Meals Delivered</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#fed7aa" }}>2-Day Trial Subscriptions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#fed7aa" }}>2-Day Trial AOV (₹)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#bbf7d0" }}>7-Day Weekly Subscriptions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#bbf7d0" }}>7-Day Weekly AOV (₹)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#bfdbfe" }}>28-Day Monthly Subscriptions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#bfdbfe" }}>28-Day Monthly AOV (₹)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#e9d5ff" }}>Custom Duration Subscriptions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#e9d5ff" }}>Custom Duration AOV (₹)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {misData.monthlyProductPlanBreakdown.map((row: any) => {
                          const isOverall = row.isOverall || row.month === "OVERALL";
                          return (
                            <TableRow
                              key={row.month}
                              hover
                              sx={{
                                backgroundColor: isOverall ? "#f8fafc" : "inherit",
                                borderTop: isOverall ? "2px solid #0f172a" : "inherit",
                                "& td": {
                                  fontWeight: isOverall ? 800 : 500,
                                  fontSize: "0.8rem",
                                },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 800, color: isOverall ? "#0f172a" : "#334155" }}>
                                {row.month}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800, color: isOverall ? "#0f172a" : "#2563eb" }}>
                                {row.subOrdersReceived}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: isOverall ? 800 : 600 }}>
                                {row.ordersDelivered.toLocaleString("en-IN")}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: isOverall ? 800 : 600 }}>
                                {row.mealsDelivered.toLocaleString("en-IN")}
                              </TableCell>
                              <TableCell align="right" sx={{ color: "#c2410c" }}>{row.trialSubs}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>₹{row.trialAOV}</TableCell>
                              <TableCell align="right" sx={{ color: "#15803d" }}>{row.weeklySubs}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>₹{row.weeklyAOV}</TableCell>
                              <TableCell align="right" sx={{ color: "#1d4ed8" }}>{row.monthlySubs}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>₹{row.monthlyAOV}</TableCell>
                              <TableCell align="right" sx={{ color: "#7e22ce" }}>{row.customSubs}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>₹{row.customAOV}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════
            TAB 0 — Revenue & Growth
        ══════════════════════════════════════════════════════ */}
        {activeTab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Row 1 — Revenue trend + Active users */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                gap: 3,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Card
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "none",
                    p: 3,
                  }}
                >
                  <ChartHeader
                    title="Monthly Revenue Trend (IST)"
                    info="Total revenue collected from paid invoices, grouped by calendar month in India Standard Time (IST). Each bar represents the sum of all invoice amounts marked as 'paid' that month."
                  />
                  <Box sx={{ width: "100%", height: 350 }}>
                    {revenueLoading ? (
                      <ChartLoader height={350} />
                    ) : (
                      <ResponsiveContainer>
                        <AreaChart
                          data={revenue}
                          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient
                              id="colorRevenue"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#f97316"
                                stopOpacity={0.3}
                              />
                              <stop
                                offset="95%"
                                stopColor="#f97316"
                                stopOpacity={0}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            dataKey="month"
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                          />
                          <YAxis
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => `₹${v / 1000}k`}
                          />
                          <RechartsTooltip
                            formatter={(value: any) => [
                              formatCurrency(value),
                              "Revenue",
                            ]}
                          />
                          <Area
                            type="monotone"
                            dataKey="revenue"
                            stroke="#f97316"
                            strokeWidth={3}
                            fillOpacity={1}
                            fill="url(#colorRevenue)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </Card>
              </Box>

              <Box>
                <Card
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "none",
                    p: 3,
                    height: "100%",
                  }}
                >
                  <ChartHeader
                    title="Active Users Growth"
                    info="Count of unique users who had at least one active subscription in a given month. A user is counted only once per month regardless of how many plans they hold."
                  />
                  <Box sx={{ width: "100%", height: 300 }}>
                    {retentionLoading ? (
                      <ChartLoader height={300} />
                    ) : (
                      retention && (
                        <ResponsiveContainer>
                          <LineChart data={retention.monthlyActiveRetention}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              vertical={false}
                              stroke="#e2e8f0"
                            />
                            <XAxis
                              dataKey="month"
                              stroke="#64748b"
                              fontSize={11}
                              tickLine={false}
                            />
                            <YAxis
                              stroke="#64748b"
                              fontSize={11}
                              tickLine={false}
                              axisLine={false}
                            />
                            <RechartsTooltip />
                            <Line
                              type="monotone"
                              dataKey="activeUsers"
                              stroke="#3b82f6"
                              strokeWidth={3}
                              dot={{ r: 4 }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 2 }}
                  >
                    Unique active users with valid subscriptions per month.
                  </Typography>
                </Card>
              </Box>
            </Box>

            {/* Row 2 — Avg Subscription Duration */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
                gap: 3,
              }}
            >
              {/* Overall stat card */}
              <Card
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "none",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {durationLoading ? (
                  <ChartLoader height={160} />
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#64748b", fontWeight: 600 }}
                      >
                        Avg. Subscription Length
                      </Typography>
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: "#f0fdf4",
                          borderRadius: "8px",
                          color: "#22c55e",
                        }}
                      >
                        <Sparkles size={20} />
                      </Box>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "baseline", gap: 1 }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 800,
                          color: "#0f172a",
                          fontFamily: "Plus Jakarta Sans",
                          lineHeight: 1,
                        }}
                      >
                        {revenueDuration?.overallAvgOrders ?? "—"}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#64748b" }}
                      >
                        orders
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1.5, display: "block" }}
                    >
                      Per subscription across{" "}
                      {(revenueDuration?.overallSubCount ?? 0).toLocaleString(
                        "en-IN",
                      )}{" "}
                      active &amp; completed subscriptions. Cancelled orders
                      excluded.
                    </Typography>
                  </>
                )}
              </Card>

              {/* Monthly bar chart */}
              <Card
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "none",
                  p: 3,
                }}
              >
                <ChartHeader
                  title="Avg. Orders per Subscription by Start Month"
                  info="For subscriptions that started in each calendar month, shows the average count of pending + completed orders. Active and completed subscriptions only. Grouped by subscription start month in IST."
                />
                <Box sx={{ width: "100%", height: 250 }}>
                  {durationLoading ? (
                    <ChartLoader height={250} />
                  ) : (
                    <ResponsiveContainer>
                      <BarChart
                        data={revenueDuration?.monthlyDuration ?? []}
                        margin={{ top: 5, right: 20, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="#e2e8f0"
                        />
                        <XAxis
                          dataKey="month"
                          stroke="#64748b"
                          fontSize={11}
                          tickLine={false}
                        />
                        <YAxis
                          stroke="#64748b"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                        />
                        <RechartsTooltip
                          formatter={(value: any, _: any, props: any) => [
                            `${value} orders avg (${props.payload.subCount} subs)`,
                            "Avg. Duration",
                          ]}
                        />
                        <Bar
                          dataKey="avgOrders"
                          fill="#22c55e"
                          radius={[4, 4, 0, 0]}
                          barSize={25}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Card>
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════
            TAB 1 — Retention Cohorts
        ══════════════════════════════════════════════════════ */}
        {activeTab === 1 &&
          (retentionLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#f97316" }} />
            </Box>
          ) : (
            retention && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 3,
                }}
              >
                <Box sx={{ gridColumn: { xs: "span 1", md: "span 2" } }}>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "#0f172a",
                                fontFamily: "Plus Jakarta Sans",
                              }}
                            >
                              Subscription Cohort Retention Heatmap
                            </Typography>
                            <InfoTip text="Groups users by the month they first subscribed (cohort). Month 0 = the cohort's starting month (always 100%). Subsequent columns show the % of that cohort still active in M+1, M+2, etc. Darker orange = higher retention." />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Tracks cohorts of users based on their sign-up month
                            and displays their active rates in subsequent
                            months.
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: "rgba(249, 115, 22, 0.15)",
                            borderRadius: "2px",
                          }}
                        />
                        <Typography variant="caption" sx={{ mr: 2 }}>
                          Low
                        </Typography>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: "rgba(249, 115, 22, 0.8)",
                            borderRadius: "2px",
                          }}
                        />
                        <Typography variant="caption">
                          High Retention
                        </Typography>
                      </Box>
                    </Box>
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        overflow: "auto",
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                color: "#475569",
                              }}
                            >
                              Cohort Month
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                color: "#475569",
                              }}
                            >
                              Cohort Size
                            </TableCell>
                            {Array.from({
                              length: Math.max(
                                ...retention.cohortMatrix.map(
                                  (c) => c.retentionPct.length,
                                ),
                                0,
                              ),
                            }).map((_, idx) => (
                              <TableCell
                                key={idx}
                                align="center"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.8rem",
                                  color: "#475569",
                                }}
                              >
                                Month {idx}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {retention.cohortMatrix.map((row) => (
                            <TableRow key={row.cohortMonth}>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: "monospace",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {row.cohortMonth}
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                              >
                                {row.cohortSize} users
                              </TableCell>
                              {row.retentionPct.map((cell) => (
                                <TableCell
                                  key={cell.offset}
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.8rem",
                                    color: getCohortTextColor(cell.percentage),
                                    backgroundColor: getCohortColor(
                                      cell.percentage,
                                    ),
                                    border: "1px solid #ffffff",
                                    minWidth: "80px",
                                    p: 1.5,
                                  }}
                                >
                                  {cell.percentage}%
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      fontSize: "0.6rem",
                                      opacity: 0.8,
                                    }}
                                  >
                                    ({cell.count} users)
                                  </Typography>
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Box>

                <Box>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                    }}
                  >
                    <ChartHeader
                      title="Active User Retention Rate (%)"
                      info="The percentage of last month's active users who are still active this month. Formula: (Users active in both this month and last month) ÷ (Users active last month) × 100."
                    />
                    <Box sx={{ width: "100%", height: 250 }}>
                      <ResponsiveContainer>
                        <BarChart data={retention.monthlyActiveRetention}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            dataKey="month"
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                          />
                          <YAxis
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                            axisLine={false}
                            unit="%"
                          />
                          <RechartsTooltip
                            formatter={(v) => [
                              `${v}%`,
                              "Active Retention Rate",
                            ]}
                          />
                          <Bar
                            dataKey="retentionRate"
                            fill="#f97316"
                            radius={[4, 4, 0, 0]}
                            barSize={25}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Card>
                </Box>

                <Box>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                    }}
                  >
                    <ChartHeader
                      title="Subscription Renewal Rate (%)"
                      info="Of all users whose subscriptions ended in a given month, this is the % who started a new subscription the following month. High renewal rates indicate strong product-market fit and customer satisfaction."
                    />
                    <Box sx={{ width: "100%", height: 250 }}>
                      <ResponsiveContainer>
                        <LineChart data={retention.monthlyRenewalRetention}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            dataKey="month"
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                          />
                          <YAxis
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                            axisLine={false}
                            unit="%"
                          />
                          <RechartsTooltip
                            formatter={(v) => [`${v}%`, "Renewal Rate"]}
                          />
                          <Line
                            type="monotone"
                            dataKey="renewalRate"
                            stroke="#10b981"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Card>
                </Box>
              </Box>
            )
          ))}

        {/* ══════════════════════════════════════════════════════
            TAB 2 — Meal Swaps
        ══════════════════════════════════════════════════════ */}
        {activeTab === 2 &&
          (swapsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#f97316" }} />
            </Box>
          ) : (
            swaps && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 3,
                }}
              >
                {/* Highest swapped bar chart */}
                <Box>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                      height: "100%",
                    }}
                  >
                    <ChartHeader
                      title="Highest Swapped Main Meals"
                      info="On each delivery day, the meal with the most non-swapped orders is treated as the 'main meal of the day'. This chart shows which of those daily main meals had the highest percentage of users opting to swap away from them."
                    />
                    <Box sx={{ width: "100%", height: 300 }}>
                      <ResponsiveContainer>
                        <BarChart
                          layout="vertical"
                          data={swaps.swappedMost}
                          margin={{ left: 20 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            horizontal={false}
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            type="number"
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                            unit="%"
                            domain={[0, 100]}
                          />
                          <YAxis
                            type="category"
                            dataKey="name"
                            stroke="#64748b"
                            fontSize={10}
                            tickLine={false}
                            width={130}
                          />
                          <RechartsTooltip
                            formatter={(value: any, _name: any, props: any) => [
                              `${value}% swap rate (${props.payload.count} swaps / ${props.payload.total} orders)`,
                              "Swap Rate",
                            ]}
                          />
                          <Bar
                            dataKey="percentage"
                            fill="#f97316"
                            radius={[0, 4, 4, 0]}
                            barSize={14}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Card>
                </Box>

                {/* All Meals table */}
                <Box>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                      height: "100%",
                    }}
                  >
                    <ChartHeader
                      title="All Meals — Swap Rate"
                      info="Complete list of all meals that have been the daily main meal at least once. Swap rate = (number of users who swapped away) ÷ (total orders on days that meal was the main) × 100. Lower is better — it means users preferred to keep this meal."
                      right={
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setMealSearch("");
                            setAllMealsOpen(true);
                          }}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            borderColor: "#e2e8f0",
                          }}
                        >
                          Expand &amp; Search
                        </Button>
                      }
                    />

                    {/* Inline preview table — top 8 */}
                    <TableContainer sx={{ maxHeight: 320, overflow: "auto" }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow
                            sx={{
                              "& th": {
                                backgroundColor: "#f8fafc",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                color: "#475569",
                              },
                            }}
                          >
                            <TableCell>Meal</TableCell>
                            <TableCell align="center">Days Main</TableCell>
                            <TableCell align="center">Swaps / Orders</TableCell>
                            <TableCell align="right">Swap Rate</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...swaps.allMeals]
                            .sort((a, b) => b.percentage - a.percentage)
                            .slice(0, 8)
                            .map((row) => (
                              <TableRow key={row.mealId} hover>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.78rem",
                                    maxWidth: 160,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.name}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontSize: "0.78rem" }}
                                >
                                  {row.daysObserved}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontSize: "0.78rem", color: "#64748b" }}
                                >
                                  {row.count} / {row.total}
                                </TableCell>
                                <TableCell align="right">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: "#f1f5f9",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          height: "100%",
                                          width: `${Math.min(row.percentage, 100)}%`,
                                          backgroundColor:
                                            row.percentage < 30
                                              ? "#10b981"
                                              : row.percentage < 50
                                                ? "#f97316"
                                                : "#ef4444",
                                          borderRadius: 3,
                                        }}
                                      />
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontSize: "0.78rem",
                                        fontWeight: 700,
                                        minWidth: 36,
                                      }}
                                    >
                                      {row.percentage}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1.5, display: "block" }}
                    >
                      Showing top 8 by swap rate. Click "Expand & Search" to see
                      all {swaps.allMeals.length} meals.
                    </Typography>
                  </Card>
                </Box>

                {/* Monthly swap trend — full width */}
                <Box sx={{ gridColumn: { xs: "span 1", md: "span 2" } }}>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                    }}
                  >
                    <ChartHeader
                      title="Monthly Overall Swap Rate (%)"
                      info="The percentage of all orders in a given month where the user chose to swap from the default main meal. Computed as: (total swapped orders) ÷ (total non-cancelled orders) × 100, grouped by delivery month in IST."
                    />
                    <Box sx={{ width: "100%", height: 250 }}>
                      <ResponsiveContainer>
                        <LineChart data={swaps.monthlyTrend}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            dataKey="month"
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                          />
                          <YAxis
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                            axisLine={false}
                            unit="%"
                          />
                          <RechartsTooltip
                            formatter={(v) => [
                              `${parseFloat(v as string).toFixed(2)}%`,
                              "Swap Rate",
                            ]}
                          />
                          <Line
                            type="monotone"
                            dataKey="swapPercentage"
                            stroke="#ea580c"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Card>
                </Box>
              </Box>
            )
          ))}

        {/* ══════════════════════════════════════════════════════
            TAB 3 — User Rankings
        ══════════════════════════════════════════════════════ */}
        {activeTab === 3 &&
          (rankingsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#f97316" }} />
            </Box>
          ) : (
            rankings && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                  gap: 3,
                }}
              >
                {/* Top Spenders */}
                <Box>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: "#fff7ed",
                          borderRadius: "8px",
                          color: "#f97316",
                        }}
                      >
                        <Award size={20} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#0f172a",
                              fontFamily: "Plus Jakarta Sans",
                            }}
                          >
                            Top Spenders
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Highest lifetime revenue contributions
                          </Typography>
                        </Box>
                        <InfoTip text="Ranked by the total amount across all paid invoices linked to this user. Includes all subscription cycles." />
                      </Box>
                    </Box>
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                      }}
                    >
                      <Table size="small">
                        <TableBody>
                          {rankings.highestPaid.map((user, idx) => (
                            <TableRow key={user.userId} hover>
                              <TableCell
                                sx={{
                                  width: 40,
                                  fontWeight: 700,
                                  color: "#64748b",
                                  fontSize: "0.8rem",
                                }}
                              >
                                #{idx + 1}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem", p: 1.5 }}>
                                <Typography
                                  sx={{ fontWeight: 700, fontSize: "0.8rem" }}
                                >
                                  {user.name}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "0.65rem", color: "#64748b" }}
                                >
                                  {user.phoneNumber}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: "0.8rem",
                                  color: "#f97316",
                                }}
                              >
                                {formatCurrency(user.totalSpent || 0)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Box>

                {/* Longest Active */}
                <Box>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: "#f0fdf4",
                          borderRadius: "8px",
                          color: "#22c55e",
                        }}
                      >
                        <Clock size={20} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#0f172a",
                              fontFamily: "Plus Jakarta Sans",
                            }}
                          >
                            Longest Cohort Users
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Days from first to latest subscription
                          </Typography>
                        </Box>
                        <InfoTip text="Ranked by total customer lifespan: the number of days between their very first subscription start date and their latest subscription end date (or today if still active). Higher days = longer relationship with Kinnow." />
                      </Box>
                    </Box>
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                      }}
                    >
                      <Table size="small">
                        <TableBody>
                          {rankings.longestActive.map((user, idx) => (
                            <TableRow key={user.userId} hover>
                              <TableCell
                                sx={{
                                  width: 40,
                                  fontWeight: 700,
                                  color: "#64748b",
                                  fontSize: "0.8rem",
                                }}
                              >
                                #{idx + 1}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem", p: 1.5 }}>
                                <Typography
                                  sx={{ fontWeight: 700, fontSize: "0.8rem" }}
                                >
                                  {user.name}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "0.65rem", color: "#64748b" }}
                                >
                                  {user.phoneNumber}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                              >
                                {user.daysAsCustomer != null ? (
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontWeight: 800,
                                        fontSize: "0.82rem",
                                        color: "#16a34a",
                                      }}
                                    >
                                      {user.daysAsCustomer} days
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "0.6rem",
                                        color: "#64748b",
                                      }}
                                    >
                                      {user.firstStartDate
                                        ? new Date(
                                            user.firstStartDate,
                                          ).toLocaleDateString("en-IN", {
                                            day: "numeric",
                                            month: "short",
                                            year: "2-digit",
                                          })
                                        : ""}
                                      {" → "}
                                      {user.latestEndDate
                                        ? new Date(
                                            user.latestEndDate,
                                          ).toLocaleDateString("en-IN", {
                                            day: "numeric",
                                            month: "short",
                                            year: "2-digit",
                                          })
                                        : "now"}
                                    </Typography>
                                  </Box>
                                ) : (
                                  "N/A"
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Box>

                {/* Most Subscriptions */}
                <Box>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "none",
                      p: 3,
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: "#eff6ff",
                          borderRadius: "8px",
                          color: "#3b82f6",
                        }}
                      >
                        <Sparkles size={20} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#0f172a",
                              fontFamily: "Plus Jakarta Sans",
                            }}
                          >
                            Most Loyal Fans
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Highest counts of total subscriptions
                          </Typography>
                        </Box>
                        <InfoTip text="Total number of subscription records (across all statuses) linked to this user. A high count means the user has renewed or purchased multiple plans over time." />
                      </Box>
                    </Box>
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                      }}
                    >
                      <Table size="small">
                        <TableBody>
                          {rankings.mostSubscriptions.map((user, idx) => (
                            <TableRow key={user.userId} hover>
                              <TableCell
                                sx={{
                                  width: 40,
                                  fontWeight: 700,
                                  color: "#64748b",
                                  fontSize: "0.8rem",
                                }}
                              >
                                #{idx + 1}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem", p: 1.5 }}>
                                <Typography
                                  sx={{ fontWeight: 700, fontSize: "0.8rem" }}
                                >
                                  {user.name}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "0.65rem", color: "#64748b" }}
                                >
                                  {user.phoneNumber}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: "0.8rem",
                                  color: "#3b82f6",
                                }}
                              >
                                {user.count} plans
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Box>
              </Box>
            )
          ))}
      </Box>

      {/* ══════════════════════════════════════════════════════
          ALL MEALS DIALOG
      ══════════════════════════════════════════════════════ */}
      <Dialog
        open={allMealsOpen}
        onClose={() => setAllMealsOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontFamily: "Plus Jakarta Sans" }}
                >
                  All Meals — Swap Rate
                </Typography>
                <InfoTip text="Swap Rate = users who swapped away ÷ total orders on days this meal was the daily main × 100. Lower is better." />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {filteredMeals.length} of {swaps?.allMeals.length ?? 0} meals
                shown · click column headers to sort
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setAllMealsOpen(false)}
              sx={{ color: "#64748b" }}
            >
              <X size={18} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box sx={{ px: 3, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search meal name…"
            value={mealSearch}
            onChange={(e) => setMealSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={15} color="#94a3b8" />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Box>

        <DialogContent sx={{ px: 3, pt: 1 }}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      backgroundColor: "#f8fafc",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: "#475569",
                    },
                  }}
                >
                  <TableCell sx={{ minWidth: 200 }}>Meal Name</TableCell>
                  <TableCell
                    align="center"
                    sortDirection={
                      mealSortField === "daysObserved" ? mealSortDir : false
                    }
                  >
                    <TableSortLabel
                      active={mealSortField === "daysObserved"}
                      direction={
                        mealSortField === "daysObserved" ? mealSortDir : "desc"
                      }
                      onClick={() => handleMealSort("daysObserved")}
                    >
                      Days Main
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="center"
                    sortDirection={
                      mealSortField === "total" ? mealSortDir : false
                    }
                  >
                    <TableSortLabel
                      active={mealSortField === "total"}
                      direction={
                        mealSortField === "total" ? mealSortDir : "desc"
                      }
                      onClick={() => handleMealSort("total")}
                    >
                      Total Orders
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="center"
                    sortDirection={
                      mealSortField === "count" ? mealSortDir : false
                    }
                  >
                    <TableSortLabel
                      active={mealSortField === "count"}
                      direction={
                        mealSortField === "count" ? mealSortDir : "desc"
                      }
                      onClick={() => handleMealSort("count")}
                    >
                      Swapped Out
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sortDirection={
                      mealSortField === "percentage" ? mealSortDir : false
                    }
                  >
                    <TableSortLabel
                      active={mealSortField === "percentage"}
                      direction={
                        mealSortField === "percentage" ? mealSortDir : "desc"
                      }
                      onClick={() => handleMealSort("percentage")}
                    >
                      Swap Rate
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Retention</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMeals.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{ py: 6, color: "#94a3b8" }}
                    >
                      No meals match your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMeals.map((row) => (
                    <TableRow key={row.mealId} hover>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
                        {row.name}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {row.daysObserved}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {row.total}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {row.count}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 60,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: "#f1f5f9",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                height: "100%",
                                width: `${Math.min(row.percentage, 100)}%`,
                                backgroundColor:
                                  row.percentage < 30
                                    ? "#10b981"
                                    : row.percentage < 50
                                      ? "#f97316"
                                      : "#ef4444",
                                borderRadius: 3,
                              }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "0.82rem",
                              fontWeight: 700,
                              minWidth: 40,
                            }}
                          >
                            {row.percentage}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <RetentionBadge pct={row.percentage} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAllMealsOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Download MIS Report Modal ── */}
      <Dialog
        open={downloadModalOpen}
        onClose={() => !downloadLoading && setDownloadModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1, borderRadius: "8px", backgroundColor: "#f0fdf4", color: "#16a34a" }}>
                <FileSpreadsheet size={22} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontFamily: "Plus Jakarta Sans" }}>
                  Export Financial MIS Reports
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Select export format and report granularity
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setDownloadModalOpen(false)} disabled={downloadLoading}>
              <X size={18} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[
              {
                id: "master",
                title: "Complete 10-Sheet Master MIS Workbook",
                badge: "OFFICIAL MASTER",
                desc: "Full comprehensive multi-tab Excel workbook with Executive Dashboard, Traction Build-Up, Customer Cohorts Funnel, 16-Pathway Renewal Matrix, AOV by Plan, Referrals, Geography, and Feedback.",
                icon: <Layers size={20} color="#16a34a" />,
                bg: "#f0fdf4",
                border: "#bbf7d0",
              },
              {
                id: "summary",
                title: `Filtered Period MIS Summary (${startDate} → ${endDate})`,
                badge: "DYNAMIC PERIOD",
                desc: "Export summary tables, KPIs, and cohort funnels calculated strictly for your currently selected date filter period.",
                icon: <Calendar size={20} color="#3b82f6" />,
                bg: "#eff6ff",
                border: "#bfdbfe",
              },
              {
                id: "ledger",
                title: "Raw Master Invoices Ledger",
                badge: "RAW AUDIT LEDGER",
                desc: "Line-by-line export of all master invoices with UID, timestamps, customer names, phone numbers, product plan categories, and amounts.",
                icon: <FileText size={20} color="#8b5cf6" />,
                bg: "#faf5ff",
                border: "#e9d5ff",
              },
            ].map((opt) => {
              const selected = downloadOption === opt.id;
              return (
                <Paper
                  key={opt.id}
                  elevation={0}
                  onClick={() => setDownloadOption(opt.id as any)}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    cursor: "pointer",
                    border: selected ? "2px solid #16a34a" : "1px solid #e2e8f0",
                    backgroundColor: selected ? opt.bg : "#ffffff",
                    transition: "all 0.15s ease-in-out",
                    "&:hover": {
                      borderColor: selected ? "#16a34a" : "#cbd5e1",
                      backgroundColor: selected ? opt.bg : "#f8fafc",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ p: 0.75, borderRadius: "8px", backgroundColor: "white", border: `1px solid ${opt.border}` }}>
                        {opt.icon}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                        {opt.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={opt.badge}
                      size="small"
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 800,
                        backgroundColor: "white",
                        border: `1px solid ${opt.border}`,
                        height: 20,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: "#475569", fontSize: "0.8rem", mt: 0.75, pl: 4.5 }}>
                    {opt.desc}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, display: "flex", justifyContent: "space-between" }}>
          <Button onClick={() => setDownloadModalOpen(false)} disabled={downloadLoading} sx={{ textTransform: "none", color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExecuteDownload}
            disabled={downloadLoading}
            startIcon={downloadLoading ? <CircularProgress size={16} color="inherit" /> : <Download size={16} />}
            sx={{
              borderRadius: "8px",
              backgroundColor: "#16a34a",
              color: "white",
              textTransform: "none",
              fontSize: "0.85rem",
              fontWeight: 700,
              px: 3,
              "&:hover": { backgroundColor: "#15803d" },
            }}
          >
            {downloadLoading ? "Generating Export..." : "Download Excel (.xlsx)"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default function AnalyticsPage() {
  const { admin } = useAuth();
  return admin && !["admin", "super-admin"].includes(admin.role)
    ? <KitchenOverview /> : <Analytics />;
}
